'use client';
import Image from 'next/image';
import { motion, useInView } from 'motion/react';
import { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, A11y } from 'swiper/modules';
import { type Swiper as SwiperClass } from 'swiper';
import { cn } from 'lib/utils';
import { type BloomProps, Bloom, HeaderSection, IconButton, Wrapper } from 'ui';

export type LogoCarouselProps = {
  className?: string;
  eyebrow?: string;
  title?: string;
  intro?: string;
  logos: {
    src: string;
    alt: string;
    width: number;
    height: number;
  }[];
  bloom?: BloomProps;
};

const MIN_SLIDES = 24;
const SLIDE_SPEED = 1750;
const MAX_LOGO_WIDTH = 120;
const MAX_LOGO_HEIGHT = 94;

const LogoCarousel = ({
  className,
  eyebrow,
  title,
  intro,
  logos,
  bloom,
}: LogoCarouselProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-20%' });
  const swiperRef = useRef<SwiperClass | null>(null);
  const [isPlaying, setIsPlaying] = useState(true);

  const slides = [];
  while (slides.length < MIN_SLIDES) slides.push(...logos);

  const togglePlay = () => {
    const swiper = swiperRef.current;
    if (!swiper?.autoplay) return;

    if (isPlaying) {
      swiper.autoplay.stop();
      setIsPlaying(false);
    } else {
      swiper.autoplay.start();
      setIsPlaying(true);
    }
  };

  return (
    <motion.section {...{ ref }}>
      <Wrapper className={cn('relative flex flex-col gap-12', className)}>
        <Bloom {...bloom} />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <HeaderSection
            className="relative z-2"
            content={intro}
            alignment="center"
            eyebrowAsTitle={!!eyebrow}
            {...{
              eyebrow,
              title,
            }}
          />
        </motion.div>

        <motion.div
          className="relative z-2 flex flex-col gap-2.5"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <Swiper
            modules={[Autoplay, A11y]}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;

              // Start paused if the user prefers reduced motion.
              if (
                typeof window !== 'undefined' &&
                window.matchMedia('(prefers-reduced-motion: reduce)').matches
              ) {
                swiper.autoplay?.stop();
                setIsPlaying(false);
              }
            }}
            slidesPerView="auto"
            spaceBetween={80}
            loop
            loopAdditionalSlides={logos.length}
            // Keep scrolling even if the track happens to fit the viewport.
            watchOverflow={false}
            speed={SLIDE_SPEED}
            autoplay={{
              delay: 0,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            allowTouchMove
            aria-label={title || 'Partner logos'}
            // Linear timing makes the slide-by-slide autoplay read as continuous.
            className="h-41.5 w-full [&_.swiper-wrapper]:!ease-linear"
          >
            {slides.map((logo, index) => {
              const scale = Math.min(
                MAX_LOGO_WIDTH / logo.width,
                MAX_LOGO_HEIGHT / logo.height
              );

              return (
                <SwiperSlide
                  key={`${logo.src}-${index}`}
                  className="flex !w-auto items-center"
                >
                  <Image
                    src={logo.src}
                    alt={logo.alt}
                    width={logo.width}
                    height={logo.height}
                    draggable={false}
                    className="object-contain select-none"
                    style={{
                      width: Math.round(logo.width * scale),
                      height: Math.round(logo.height * scale),
                    }}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>

          <div className="flex justify-end">
            <IconButton
              icon={isPlaying ? 'pause' : 'play'}
              label={isPlaying ? 'Pause logo animation' : 'Play logo animation'}
              aria-pressed={!isPlaying}
              onClick={togglePlay}
              iconClassName="w-4"
            />
          </div>
        </motion.div>
      </Wrapper>
    </motion.section>
  );
};

export default LogoCarousel;
